import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSignOut, useIsMobile } from '@/hooks';
import { useDeleteUserAccountMutation } from '@/store/api';
import { toast } from '@/components/ui/use-toast';
import { UserRole } from '@sportspredict/shared';
import { cn } from '@/utils';
import { Link } from 'react-router';
import { ROUTES } from '@/constants';
import ExportDataButton from '../components/ExportDataButton';
import { CookieSettingsModal } from '@/components/core/CookieSettingsModal';

export default function AccountModal({
	open,
	onOpenChange,
	appVersion = '1.0.0',
	setSidebarOpen,
	user,
}) {
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
	const signOut = useSignOut();
	const isMobile = useIsMobile();
	const [deleteUserAccount] = useDeleteUserAccountMutation();

	const confirmDeleteAccount = async () => {
		if (!user?.id) return;
		if (user.role === UserRole.Admin) {
			toast({
				title: "Users with the role 'admin' are not allowed to delete their accounts.",
				variant: 'destructive',
			});
			setConfirmDeleteOpen(false);
			return;
		}
		try {
			await deleteUserAccount(user.id).unwrap();
			setConfirmDeleteOpen(false);
			onOpenChange?.(false);
			signOut();
		} catch {
			toast({
				title: 'Something went wrong',
				description: 'Error while deleting account',
				variant: 'destructive',
			});
			setConfirmDeleteOpen(false);
		}
	};

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent
					className={cn(
						"bg-[#101423] border border-[#1E2748] p-0 font-['DM_Sans'] backdrop-blur-xl overflow-hidden flex flex-col",
						!isMobile && 'w-[90vw] max-w-[628px] h-[90vh] max-h-[824px] rounded-2xl',
						isMobile && 'w-screen h-screen disableClose'
					)}
					disableClose={isMobile}
				>
					{/* Header */}
					<DialogHeader className="px-6 pt-[48px] pb-[26px]">
						<div className="flex items-center justify-between">
							{isMobile ? (
								<DialogClose asChild>
									<div className="flex items-center text-[#B5B6BA]">
										<ChevronLeft className="w-6 h-6" />
										<span>Back</span>
									</div>
								</DialogClose>
							) : (
								<div />
							)}
							<DialogTitle className="text-center text-[#F7F7F8] text-[18px] font-bold">
								Account Settings
							</DialogTitle>
							<div />
							{isMobile && <div />}
						</div>
						<DialogDescription className="sr-only">Manage your account</DialogDescription>
					</DialogHeader>

					{/* Flat list with thin separators */}
					<div className="px-0">
						<div className="border-t border-b border-[#2D3A6C] divide-y divide-[#2D3A6C]">
							<ExportDataButton user={user} open={open} />
							<button
								type="button"
								className="w-full h-14 px-6 flex items-center justify-between bg-transparent text-[13px] text-[#F7F7F8] hover:bg-white/5"
								onClick={() => setCookieSettingsOpen(true)}
							>
								<span className="text-sm font-normal">Cookie Management</span>
								<ChevronRight className="w-5 h-5 text-[#D4D5D8]" />
							</button>
							<Link
								to={ROUTES.ACCOUNT}
								prefetch="viewport"
								className="
                  w-full h-14
                  px-6
                  flex items-center justify-between
                  bg-transparent
                  text-[13px] text-[#E87D7D]
                  hover:bg-white/5
                "
								onClick={() => {
									setSidebarOpen(false);
									onOpenChange?.(false);
								}}
							>
								<span className="text-sm font-normal">Delete Account</span>
								<ChevronRight className="w-5 h-5 text-[#D4D5D8]" />
							</Link>
						</div>
					</div>

					<div className="mt-auto px-6 pb-[54px]">
						<div className="text-center">
							<div className="text-[10px] uppercase tracking-wide text-white/50">App Version</div>
							<div className="text-white/80 text-sm font-semibold mt-1">{appVersion}</div>
						</div>
					</div>

					<DialogFooter className="hidden" />
				</DialogContent>
			</Dialog>

			{/* Confirm Delete */}
			<Dialog open={confirmDeleteOpen}>
				<DialogContent
					disableClose={true}
					className="bg-black/90 border-white/10 font-['DM_Sans'] backdrop-blur-xl p-[24px]"
				>
					<DialogHeader>
						<DialogTitle className="text-left text-[1rem] text-[#DA3030]">
							Are you sure?
						</DialogTitle>
						<DialogDescription className="text-left font-[14px] text-gray-400 pt-[12px]">
							<span className="font-bold">This can't be undone.</span> All your data will be removed
							and you'll be signed out immediately.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="justify-between flex flex-row align-between pt-[12px]">
						<Button
							onClick={() => setConfirmDeleteOpen(false)}
							className="font-['DM_Sans'] bg-transparent hover:bg-transparent font-bold text-gray-100 font-[14px]"
							style={{ boxShadow: 'none' }}
						>
							Cancel
						</Button>
						<Button
							onClick={confirmDeleteAccount}
							className="bg-[#DA3030] font-['DM_Sans'] border-none font-bold text-gray-100 font-[14px] rounded-[30px]"
						>
							Yes, Delete Account
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<CookieSettingsModal open={cookieSettingsOpen} onClose={() => setCookieSettingsOpen(false)} />
		</>
	);
}
